import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";
import { useUiLabels } from "../lib/ui-labels";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out",
        className,
      )}
      {...props}
    />
  );
}

export function DialogContent({
  className,
  children,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content>) {
  const labels = useUiLabels();
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-2 z-50 grid w-[calc(100%-1rem)] max-w-lg -translate-x-1/2 gap-4",
          // The column is minmax(0,1fr) rather than auto so a wide child — a table with a minimum
          // width — can shrink and scroll inside its own wrapper. With an auto track the table made
          // the whole dialog wider than itself, and the footer button went with it: "Zapakuj" was
          // cut off 25 px past the right edge (UX-13).
          "grid-cols-[minmax(0,1fr)]",
          "max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain rounded-xl border border-border bg-card p-4 shadow-xl",
          "sm:top-1/2 sm:max-h-[calc(100dvh-2rem)] sm:-translate-y-1/2 sm:p-6",
          className,
        )}
        {...props}
      >
        {children}
        {/* A 40 px target with the same 16 px cross where it always was: the icon alone was a
            16 px target, the smallest thing to hit on a phone and the one everybody needs. */}
        <DialogPrimitive.Close
          className={cn(
            "absolute right-1 top-1 inline-flex size-10 items-center justify-center rounded-md text-muted-foreground transition-colors",
            "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
          aria-label={labels.close}
        >
          <XIcon className="size-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export function DialogHeader({ className, ...props }: ComponentProps<"div">) {
  // `pr-8` keeps a long title from running under the close button in the corner.
  return <div className={cn("flex flex-col gap-1.5 pr-8 text-left", className)} {...props} />;
}

/**
 * The buttons of a dialog.
 *
 * <p>Below `sm` the footer sticks to the bottom of the scrolling dialog, on the card's background: a
 * long form on a phone used to put "Zapisz" a whole scroll away from the field just filled. It
 * reaches over the dialog's padding (`-mx-4 -mb-4`, padding put back inside) so content scrolling
 * under it is covered edge to edge. `-bottom-4` together with the 16 px of bottom padding makes that
 * true whether the browser pins a sticky box to the scroll container's content edge or its padding
 * edge — in the second case it is only the padding that slides out of view, never a button.
 */
export function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex flex-row justify-end gap-2",
        "sticky -bottom-4 z-10 -mx-4 -mb-4 border-t border-border/70 bg-card px-4 pb-4 pt-3",
        "sm:static sm:z-auto sm:mx-0 sm:mb-0 sm:border-t-0 sm:bg-transparent sm:p-0",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("text-lg font-semibold", className)} {...props} />;
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description className={cn("text-sm text-muted-foreground", className)} {...props} />
  );
}
